import { Field, useFormState } from "react-final-form";
import { useIntl } from "react-intl";
import * as Validations from "../../common/form/validator";
import { TextInput } from "../../common/fields";
import Section from "../../common/section";

// The API's refusal when the server changes and a credential is left blank
export const SMTP_CREDENTIALS_REQUIRED = "SMTP_CREDENTIALS_REQUIRED";

// The POST body: the stored-credential flags, usesDefault and the last test
// only come from the GET, so they are not sent back
export const smtpRequest = ({
  hasCredentials,
  hasUserName,
  hasPassword,
  usesDefault,
  lastTest,
  lastSuccess,
  ...smtp
}) => smtp;

// The error code of the last SMTP update, once the API has refused it
export const smtpErrorCode = ({ action, requesting, error }) =>
  (action === "update_smtp" && !requesting && error?.response) || null;

const serverKey = (server) =>
  String(server ?? "")
    .trim()
    .toLowerCase();

const isBlank = (value) => String(value ?? "").trim() === "";

// A saved username or password: shown read only, and as the placeholder of
// the blank field while it can be kept
export const SAVED_CREDENTIAL_MASK = "********";

// One numbered step of the SMTP tab, a section like the Company sign-in tab's
const Step = ({ titleId, helpId, children }) => {
  const intl = useIntl();
  return (
    <Section title={titleId} className="smtp-step">
      <p className="smtp-section-help">{intl.formatMessage({ id: helpId })}</p>
      {children}
    </Section>
  );
};

// The SMTP settings in three steps: mail server, sender, and the sign-in to
// the server. The API never returns the stored username and password, only
// whether each one is stored (hasUserName, hasPassword). A stored field may be
// left blank to keep its saved value, but only while the server is still
// savedServer: the API will not send saved credentials to a new server, so
// when the server changes both must be entered again. A field with nothing
// stored is required. disabled: the user may not change the settings.
// refused: the API refused blank credentials (SMTP_CREDENTIALS_REQUIRED).
const SMTPForm = ({
  disabled,
  savedServer = "",
  hasUserName = false,
  hasPassword = false,
  refused = false,
  usesDefault = false,
}) => {
  const intl = useIntl();
  const { values } = useFormState({ subscription: { values: true } });

  const newServerMessage = intl.formatMessage({
    id: "smtp_credentials_new_server",
  });

  const anyStored = hasUserName || hasPassword;
  const serverUnchanged = (formValues) =>
    serverKey(formValues?.server) === serverKey(savedServer);
  const canKeep = (stored, formValues) =>
    stored && !refused && serverUnchanged(formValues);

  // Reads the server from allValues, so it follows edits to the Server field
  const credentialValidator = (stored) => (value, allValues) => {
    if (canKeep(stored, allValues) || !isBlank(value)) return undefined;
    return refused || (anyStored && !serverUnchanged(allValues))
      ? newServerMessage
      : "required";
  };

  const showRefusal =
    refused && (isBlank(values?.userName) || isBlank(values?.password));

  // The API never returns a saved credential, so read only it shows masked
  // (the form value stays blank, and a blank one is never sent as the mask).
  // While it can be kept, the blank field has the mask as its placeholder and
  // a hint under it.
  const credential = (name, stored, labelId, hintId) => {
    const keep = !disabled && canKeep(stored, values);
    return (
      <div className="smtp-cell">
        <Field
          name={name}
          type="password"
          component={TextInput}
          disabled={disabled}
          required={!canKeep(stored, values)}
          label={intl.formatMessage({ id: labelId })}
          validate={credentialValidator(stored)}
          placeholder={keep ? SAVED_CREDENTIAL_MASK : ""}
          format={(value) =>
            disabled && stored && isBlank(value) ? SAVED_CREDENTIAL_MASK : value
          }
        />
        {(keep && (
          <p className="smtp-field-hint">
            {intl.formatMessage({ id: hintId })}
          </p>
        )) ||
          null}
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-4">
      <Step titleId="smtp_step_server" helpId="smtp_step_server_help">
        <div className="smtp-two-columns">
          <div className="smtp-cell">
            <Field
              name="server"
              required
              disabled={disabled}
              component={TextInput}
              label={intl.formatMessage({ id: "smtp_host" })}
              validate={Validations.required}
            />
          </div>
          <div className="smtp-cell">
            <Field
              name="port"
              type="number"
              required
              component={TextInput}
              disabled={disabled}
              label={intl.formatMessage({ id: "smtp_port" })}
              validate={Validations.required}
            />
          </div>
        </div>
        {(usesDefault && (
          <p className="smtp-section-help mt-3 mb-0">
            {intl.formatMessage({ id: "smtp_uses_default" })}
          </p>
        )) ||
          null}
      </Step>

      <Step titleId="smtp_step_sender" helpId="smtp_step_sender_help">
        <div className="smtp-two-columns">
          <div className="smtp-cell">
            <Field
              name="senderName"
              required
              component={TextInput}
              disabled={disabled}
              label={intl.formatMessage({ id: "sender_name" })}
              validate={Validations.required}
            />
          </div>
          <div className="smtp-cell">
            <Field
              name="senderEmail"
              type="email"
              required
              component={TextInput}
              disabled={disabled}
              label={intl.formatMessage({ id: "sender_email" })}
              validate={Validations.composeValidators(
                Validations.required,
                Validations.checkEmailFormat,
              )}
            />
          </div>
        </div>
      </Step>

      <Step titleId="smtp_step_sign_in" helpId="smtp_step_sign_in_help">
        <div className="smtp-two-columns">
          {credential(
            "userName",
            hasUserName,
            "smtp_username",
            "smtp_username_saved_hint",
          )}
          {credential(
            "password",
            hasPassword,
            "smtp_password",
            "smtp_password_saved_hint",
          )}
        </div>
        {(showRefusal && !disabled && (
          <div className="edexlms-field-error mt-2" role="alert">
            {newServerMessage}
          </div>
        )) ||
          null}
      </Step>
    </div>
  );
};

export default SMTPForm;
